import { Button } from "@workspace/web-ui/components/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@workspace/web-ui/components/table"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@workspace/web-ui/components/dialog";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@workspace/web-ui/components/select";
import { Check, Circle, Loader2, Pill } from "lucide-react";
import { useState } from "react";
import { useApproveRefillMutation, useRejectRefillMutation } from "~/features/dashboard/api/refills";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { patientLogsKeys } from "../../api/queries/keys";

const formatDate = (dateString: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

export interface MedicationRequestData {
  id: string;
  medication: string;
  price: string;
  requested_date: string;
  status: string;
  dosage?: string;
  duration?: string;
  refills?: number;
  instructions?: string;
}

export interface MedicationRequestsTableProps {
  cardTitle: string;
  data: MedicationRequestData[];
  patientId: string;
  program: string;
  onReview?: (index: number) => void;
  onView?: (index: number) => void;
}

const MedicationRequestsTable = ({ data, cardTitle, patientId, program }: MedicationRequestsTableProps) => {
  const queryClient = useQueryClient();
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectDetails, setRejectDetails] = useState("");
  const [reasonError, setReasonError] = useState("");

  const selectedItem = selectedIndex !== null ? data[selectedIndex] : null;
  const selectedRefillId = selectedItem?.id ?? "";

  const approveRefillMutation = useApproveRefillMutation({
    refill_id: selectedRefillId
  });

  const rejectRefillMutation = useRejectRefillMutation();

  const invalidateMedicationRequests = () => {
    queryClient.invalidateQueries({
      queryKey: [...patientLogsKeys.medication(patientId), program, "requests"]
    });
    queryClient.invalidateQueries({
      queryKey: [...patientLogsKeys.medication(patientId), program, "historic"]
    });
  };

  const handleOpenReviewModal = (index: number) => {
    setSelectedIndex(index);
    setReviewModalOpen(true);
  };

  const handleOpenViewModal = (index: number) => {
    setSelectedIndex(index);
    setViewModalOpen(true);
  };

  const handleOpenRejectModal = () => {
    setRejectModalOpen(true);
  };

  const handleCloseRejectModal = () => {
    setRejectModalOpen(false);
    setRejectReason("");
    setRejectDetails("");
    setReasonError("");
  };

  const handleConfirmReject = async () => {
    if (!rejectReason.trim()) {
      setReasonError("Reason is required.");
      return;
    }
    if (!rejectDetails.trim()) {
      setReasonError("Additional details are required.");
      return;
    }
    setReasonError("");
    
    try {
      const response = await rejectRefillMutation.mutateAsync({
        refill_id: selectedRefillId,
        reason: rejectReason,
        details: rejectDetails,
      });

      if (response.status === "success") {
        toast.success('Refill Request Rejected Successfully', {
          style: {
            background: "#10B981",
            color: "#FFFFFF",
            borderColor: "#10B981",
          } as React.CSSProperties
        });
        invalidateMedicationRequests();
        handleCloseRejectModal();
        setReviewModalOpen(false);
      }
    } catch (error) {
      console.error("Error rejecting refill request:", error);
      toast.error('Failed to reject refill request.');
    }
  };

  const handleApprove = async () => {
    try {
      const response = await approveRefillMutation.mutateAsync();

      if (response.status === "success") {
        toast.success('Refill sent to pharmacy successfully', {
          style: {
            background: "#10B981",
            color: "#FFFFFF",
            borderColor: "#10B981",
          } as React.CSSProperties
        });
        invalidateMedicationRequests();
        setReviewModalOpen(false);
      }
    } catch (error) {
      console.error("Error sending refill to pharmacy:", error);
      toast.error('Failed to send refill to pharmacy.');
    }
  };

  return (
    <div className="flex flex-col gap-4 pb-4 bg-white rounded-lg border border-[#E6E6E6] shadow-sm">
      <div className="px-6 py-4 flex gap-2 border-b border-[#DCDCDE] w-full">
        <h2 className="text-lg font-medium text-[#2D2F35] text-left">{cardTitle}</h2>
      </div>
      <div className="flex flex-col items-center justify-center gap-2">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Medication</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Requested Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item, index) => (
              <TableRow key={index}>
                <TableCell className="font-medium">{item.medication}</TableCell>
                <TableCell>{item.price}</TableCell>
                <TableCell>{formatDate(item.requested_date)}</TableCell>
                <TableCell>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.status === 'pending'
                        ? 'bg-[#FFF6E7] text-[#FFAB1F]'
                        : item.status === 'processing'
                          ? 'bg-blue-100 text-blue-700'
                          : item.status === 'fulfilled'
                            ? 'bg-green-100 text-green-700'
                            : item.status === 'rejected'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {item.status}
                  </span>
                </TableCell>
                <TableCell>
                  {item.status === 'pending' ? (
                    <Button
                      className="bg-[#4272DD] rounded-md text-white px-4 py-1 text-sm"
                      onClick={() => handleOpenReviewModal(index)}
                    >
                      Review
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="text-gray-700 rounded-md border border-gray-300 px-4 py-1 text-sm"
                      onClick={() => handleOpenViewModal(index)}
                    >
                      View
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Review Modal - Similar to refill-details UI */}
      <Dialog open={reviewModalOpen} onOpenChange={setReviewModalOpen}>
        <DialogContent className="max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-[24px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">Review Refill Request</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <span className="border-[#00C78133] text-[#00C781] text-xs font-medium py-[2px] px-[6px] rounded-[888px] border bg-[#E6FAF2] w-fit flex gap-1">
                  <Pill className="w-3.5 h-3.5 text-[#00C781]" />
                  Refill
                </span>
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#FFF6E7] text-[#FFAB1F]">
                  {selectedItem.status}
                </span>
              </div>

              {/* Request Information */}
              <div className="flex flex-col gap-4 border border-[#E6E6E6] p-4 items-start rounded-lg">
                <h3 className="text-lg font-medium">Request Information</h3>
                <div className="grid grid-cols-[120px_1fr] gap-y-2 w-full">
                  <p className="text-sm font-semibold text-[#202020CC]">Medication:</p>
                  <p className="text-sm font-normal text-[#202020CC]">{selectedItem.medication}</p>

                  <p className="text-sm font-semibold text-[#202020CC]">Dosage:</p>
                  <p className="text-sm font-normal text-[#202020CC]">{selectedItem.dosage ?? "-"}</p>

                  <p className="text-sm font-semibold text-[#202020CC]">Duration:</p>
                  <p className="text-sm font-normal text-[#202020CC]">{selectedItem.duration || "-"}</p>

                  <p className="text-sm font-semibold text-[#202020CC]">Refills:</p>
                  <p className="text-sm font-normal text-[#202020CC]">{selectedItem.refills ?? 0}</p>

                  <p className="text-sm font-semibold text-[#202020CC]">Price:</p>
                  <p className="text-sm font-normal text-[#202020CC]">{selectedItem.price}</p>

                  <p className="text-sm font-semibold text-[#202020CC]">Requested:</p>
                  <p className="text-sm font-normal text-[#202020CC]">{formatDate(selectedItem.requested_date)}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-4 w-full items-center">
                <Button
                  className="w-[133px] !bg-[#E12121] !py-[12px] px-[16px] h-[44px] rounded"
                  onClick={handleOpenRejectModal}
                >
                  Reject Refill
                </Button>
                <div className="flex-1">
                  <Button 
                    className="!text-white !rounded-[8px] !border-none w-full h-[44px] bg-[#4272DD]" 
                    onClick={handleApprove}
                    disabled={approveRefillMutation.isPending}
                  >
                    {approveRefillMutation.isPending ? (
                      <Loader2 className="w-4 h-4 text-white animate-spin" />
                    ) : (
                      "Send to Pharmacy"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={rejectModalOpen} onOpenChange={handleCloseRejectModal}>
        <DialogContent className="rounded-[24px]">
          <DialogHeader className="flex items-center justify-center">
            <DialogTitle className="text-xl font-medium">Reject Request</DialogTitle>
          </DialogHeader>
          <div>
            <p className="flex items-center mb-4 text-xl font-medium justify-center text-center">
              You are rejecting the request for {selectedItem?.medication}.
            </p>
            <label className="text-[14px] mt-4 text-[#1A202C]">
              <Select value={rejectReason} onValueChange={setRejectReason}>
                <SelectTrigger className="w-full rounded-[8px] border h-[44px] p-2 mt-2 mb-4">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="Cannot prescribe this medication">Cannot prescribe this medication</SelectItem>
                    {program === "urgent-care" && (
                                            <SelectItem value="Need more information">Need more information</SelectItem>
                                            )}
                    <SelectItem value="Medication Completed / Discontinued">Medication Completed / Discontinued</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <label className="text-[14px] mt-4 text-[#1A202C]">Please provide additional details:</label>
              <textarea
                value={rejectDetails}
                onChange={e => setRejectDetails(e.target.value)}
                maxLength={200}
                className="w-full rounded-[8px] border h-[164px] p-2 mt-2"
              />
            </label>
            {reasonError && <div className="text-red-500">{reasonError}</div>}
            <div className="flex justify-end gap-2 mt-4">
              <Button onClick={handleCloseRejectModal} className="bg-transparent hover:bg-transparent border border-[#DCDCDE] rounded-[8px] text-[#202020CC]">Cancel</Button>
              <Button
                className="!bg-[#E12121] rounded-[8px] text-white"
                disabled={rejectRefillMutation.isPending}
                onClick={handleConfirmReject}
              >
                {rejectRefillMutation.isPending ? (
                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                ) : (
                  "Confirm"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Modal - Assigned Actions Style */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-[24px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">Medication Details</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="flex flex-col gap-3 w-full">
              <div className="bg-[#F7F8FA] rounded-[8px] p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {selectedItem.status === 'fulfilled' ? (
                      <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    ) : (
                      <Circle className="w-5 h-5 text-blue-500" />
                    )}
                    <div>
                      <h3 className={`font-medium ${selectedItem.status === 'fulfilled' ? "text-green-600 line-through" : "text-[#1A202C]"}`}>
                        {selectedItem.medication}
                      </h3>
                      <p className="text-sm text-[#6B7280]">
                        Requested: {formatDate(selectedItem.requested_date)}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      selectedItem.status === 'fulfilled'
                        ? 'bg-green-100 text-green-700'
                        : selectedItem.status === 'rejected'
                          ? 'bg-red-100 text-red-700'
                          : selectedItem.status === 'processing'
                            ? 'bg-blue-100 text-blue-700'
                            : selectedItem.status === 'pending'
                              ? 'bg-[#FFF6E7] text-[#FFAB1F]'
                            : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {selectedItem.status}
                  </span>
                </div>

                <div className="bg-white rounded-md p-2 space-y-2">
                  {/* <h4 className="font-medium text-[#1A202C]">
                    {selectedItem.medication}
                  </h4> */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-[#6B7280]">
                    <div>
                      <span className="font-medium">Dosage: </span>
                      {selectedItem.dosage ?? "-"}
                    </div>
                    <div>
                      <span className="font-medium">Duration: </span>
                      {selectedItem.duration || "-"}
                    </div>
                    <div className="md:col-span-2">
                      <span className="font-medium">Instructions: </span>
                      <span>{selectedItem.instructions ?? "-"}</span>
                    </div>
                    <div>
                      <span className="font-medium">Refills: </span>
                      {selectedItem.refills ?? "-"}
                    </div>
                    <div>
                      <span className="font-medium">Price: </span>
                      {selectedItem.price}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default MedicationRequestsTable

